import { jsx, jsxs } from 'react/jsx-runtime';
import * as Toolbar from '@radix-ui/react-toolbar';
import { BubbleMenu } from '@tiptap/react';
import { useState, useEffect } from 'react';
import { EMBED_RESOURCE_TYPE_NAME } from './contants.js';
import { styled } from 'styled-components';
import { mediaHover } from '@fabric-app/ui/constants/styled-utils';

const Root = styled(Toolbar.Root).withConfig({
  displayName: "FloatingEmbedResourceToolbar__Root",
  componentId: "sc-22d4ab20-0"
})([
  `display:flex;padding:3px;width:100%;min-width:max-content;border-radius:6px;background:rgba(var(--color-surface-over),0.92);backdrop-filter:blur(8px);box-shadow:var(--shadow-over);[role='group']{display:flex;align-items:center;gap:2px;}`
]);
const Item = styled(Toolbar.ToggleItem).withConfig({
  displayName: "FloatingEmbedResourceToolbar__Item",
  componentId: "sc-22d4ab20-1"
})([
  `border-radius:6px;border:none;padding:0 0.5rem;display:flex;align-items:center;justify-content:center;height:1.75rem;min-width:1.75rem;margin:0;outline:none;font-weight:500;color:rgba(var(--color-foreground),0.8);background:none;font-size:0.775rem;`,
  `{&:hover{color:rgba(var(--color-foreground),1);background:rgba(var(--color-foreground),0.1);}}&:focus{position:relative;box-shadow:0 0 0 2px rgb(var(--color-primary));}&:active,.item[data-state='on']{color:rgba(var(--color-primary),1);background:rgba(var(--color-primary),var(--opacity-surface-selection));}`
], mediaHover);
const sizesMap = {
  small: "100px",
  medium: "200px",
  large: "400px",
  "best-fit": "100%",
  "original-size": "auto"
};
const FloatingEmbedResourceToolbar = ({ editor }) => {
  const [imageSize, setImageSize] = useState("best-fit");
  useEffect(() => {
    if (!editor) return;
    const getImageSize = () => {
      const width = editor.getAttributes(EMBED_RESOURCE_TYPE_NAME).width;
      setImageSize(Object.keys(sizesMap).find((key) => sizesMap[key] === width) || "best-fit");
    };
    getImageSize();
    editor.on("selectionUpdate", getImageSize);
    editor.on("transaction", getImageSize);
    return () => {
      editor.off("selectionUpdate", getImageSize);
      editor.off("transaction", getImageSize);
    };
  }, [
    editor
  ]);
  const handleImageSizeChange = (value) => {
    if (!editor) return;
    const activeElement = editor.view.dom.querySelector(".ProseMirror-selectednode");
    if (!activeElement) return;
    const pos = editor.view.posAtDOM(activeElement, 0);
    const img = activeElement.querySelector("img");
    img?.style.setProperty("width", sizesMap[value]);
    img?.style.setProperty("height", "auto");
    editor.chain().focus().updateAttributes(EMBED_RESOURCE_TYPE_NAME, {
      width: sizesMap[value],
      height: "auto"
    }).setNodeSelection(pos).run();
  };
  return editor ? /* @__PURE__ */ jsx("div", {
    className: "absolute",
    children: /* @__PURE__ */ jsx(BubbleMenu, {
      editor,
      shouldShow: ({ editor: editor2, from, to }) => {
        let shouldShow = false;
        editor2.state.doc.descendants((node, pos) => {
          if (pos >= from && pos <= to && node.type.name === EMBED_RESOURCE_TYPE_NAME && node.attrs.kind === "image") {
            shouldShow = true;
          }
          return false;
        });
        return shouldShow;
      },
      pluginKey: "floating-embed-resource-toolbar",
      tippyOptions: {
        placement: "bottom",
        delay: 250,
        moveTransition: "transform 0.2s ease-out"
      },
      children: /* @__PURE__ */ jsx(Root, {
        "aria-label": "Formatting options",
        children: /* @__PURE__ */ jsxs(Toolbar.ToggleGroup, {
          type: "single",
          "aria-label": "Text formatting",
          value: imageSize,
          onValueChange: handleImageSizeChange,
          children: [
            /* @__PURE__ */ jsx(Item, {
              value: "small",
              "aria-label": "Small",
              children: "Small"
            }),
            /* @__PURE__ */ jsx(Item, {
              value: "medium",
              "aria-label": "Medium",
              children: "Medium"
            }),
            /* @__PURE__ */ jsx(Item, {
              value: "large",
              "aria-label": "Large",
              children: "Large"
            }),
            /* @__PURE__ */ jsx(Item, {
              value: "best-fit",
              "aria-label": "Best fit",
              children: "Best fit"
            }),
            /* @__PURE__ */ jsx(Item, {
              value: "original-size",
              "aria-label": "Original size",
              children: "Original size"
            })
          ]
        })
      })
    })
  }) : null;
};

export { FloatingEmbedResourceToolbar };
//# sourceMappingURL=FloatingEmbedResourceToolbar.js.map
